export const SCALE_CONFIG = {
  // Global scale factor - change this single value to scale everything
  GLOBAL_SCALE: 0.8, // 80% of original size
  
  // Scale utilities
  scale: (value) => {
    if (typeof value === 'string') {
      // Handle rem, px, em values
      const match = value.match(/^(\d*\.?\d+)(rem|px|em)$/);
      if (match) {
        const [, num, unit] = match;
        return `${parseFloat(num) * SCALE_CONFIG.GLOBAL_SCALE}${unit}`;
      }
      return value;
    }
    if (typeof value === 'number') {
      return value * SCALE_CONFIG.GLOBAL_SCALE;
    }
    return value;
  },
  
  // Scale object values recursively
  scaleObject: (obj) => {
    const scaled = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        scaled[key] = SCALE_CONFIG.scaleObject(value);
      } else {
        scaled[key] = SCALE_CONFIG.scale(value);
      }
    }
    return scaled;
  }
};

export const BRAND_PALETTE = {
  SLATE: '#475569',      // Blue-Grey Primary
  STEEL: '#64748B',      // Slate Grey Secondary  
  LIGHT: '#F8FAFC',      // Off-White Grey Light
  DARK: '#0F172A'        // Dark Blue-Grey
};

// Base responsive configuration (unscaled)
const BASE_RESPONSIVE_CONFIG = {
  // Breakpoints
  breakpoints: {
    xs: '475px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px'
  },

  // Device detection
  devices: {
    mobile: { maxWidth: 768 },
    tablet: { minWidth: 769, maxWidth: 1023 },
    desktop: { minWidth: 1024 }
  },

  // Spacing for mobile vs desktop
  spacing: {
    mobile: {
      xs: '0.25rem',   // 4px
      sm: '0.5rem',    // 8px
      md: '0.75rem',   // 12px
      lg: '1rem',      // 16px
      xl: '1.5rem',    // 24px
      '2xl': '2rem',   // 32px
      '3xl': '2.5rem', // 40px
      '4xl': '3rem'    // 48px
    },
    desktop: {
      xs: '0.5rem',    // 8px
      sm: '0.75rem',   // 12px
      md: '1rem',      // 16px
      lg: '1.5rem',    // 24px
      xl: '2rem',      // 32px
      '2xl': '3rem',   // 48px
      '3xl': '4rem',   // 64px
      '4xl': '5rem'    // 80px
    }
  },

  // Typography scales
  typography: {
    mobile: {
      'text-xs': '0.75rem',
      'text-sm': '0.875rem',
      'text-base': '1rem',
      'text-lg': '1.125rem',
      'text-xl': '1.25rem',
      'text-2xl': '1.5rem',
      'text-3xl': '1.875rem'
    },
    desktop: {
      'text-xs': '0.75rem',
      'text-sm': '0.875rem',
      'text-base': '1rem',
      'text-lg': '1.125rem',
      'text-xl': '1.25rem',
      'text-2xl': '1.5rem',
      'text-3xl': '1.875rem',
      'text-4xl': '2.25rem',
      'text-5xl': '3rem'
    }
  },

  // Component sizes
  components: {
    button: {
      mobile: {
        sm: { padding: '0.5rem 1rem', fontSize: '0.875rem', height: '2.5rem' },
        md: { padding: '0.75rem 1.5rem', fontSize: '1rem', height: '3rem' },
        lg: { padding: '1rem 2rem', fontSize: '1.125rem', height: '3.5rem' }
      },
      desktop: {
        sm: { padding: '0.5rem 1rem', fontSize: '0.875rem', height: '2.75rem' },
        md: { padding: '0.75rem 1.5rem', fontSize: '1rem', height: '3.25rem' },
        lg: { padding: '1rem 2rem', fontSize: '1.125rem', height: '3.75rem' },
        xl: { padding: '1.25rem 2.5rem', fontSize: '1.25rem', height: '4rem' }
      }
    },
    input: {
      mobile: {
        sm: { padding: '0.5rem 0.75rem', fontSize: '0.875rem', height: '2.5rem' },
        md: { padding: '0.75rem 1rem', fontSize: '1rem', height: '3rem' },
        lg: { padding: '1rem 1.25rem', fontSize: '1.125rem', height: '3.5rem' }
      },
      desktop: {
        sm: { padding: '0.5rem 0.75rem', fontSize: '0.875rem', height: '2.75rem' },
        md: { padding: '0.75rem 1rem', fontSize: '1rem', height: '3.25rem' },
        lg: { padding: '1rem 1.25rem', fontSize: '1.125rem', height: '3.75rem' }
      }
    },
    sidebar: {
      mobile: { width: '280px', padding: '1rem' },
      desktop: { width: '320px', padding: '1.5rem' }
    },
    card: {
      mobile: { padding: '1rem', borderRadius: '0.75rem', gap: '0.75rem' },
      desktop: { padding: '1.5rem', borderRadius: '1rem', gap: '1rem' }
    }
  }
};

// Apply scaling to responsive configuration
export const RESPONSIVE_CONFIG = SCALE_CONFIG.scaleObject(BASE_RESPONSIVE_CONFIG);

// Blue-Grey Professional Theme Configuration
export const THEME_CONFIG = {
  light: {
    // Navigation/UI Colors - Blue-Grey Professional
    'color-navigation': BRAND_PALETTE.SLATE,           // #475569 - Blue-grey for sidebar
    'color-navigation-hover': '#334155',              // Darker blue-grey on hover
    'color-navigation-active': '#64748B',             // Lighter blue-grey for active states
    'color-navigation-text': '#FFFFFF',               // White text on navigation
    
    // Accent/Interactive Colors - Subtle blue accent
    'color-accent': '#3B82F6',                        // Professional blue for buttons, links
    'color-accent-hover': '#2563EB',                  // Darker blue on hover
    'color-accent-active': '#1D4ED8',                 // Even darker blue for active
    'color-accent-subtle': '#DBEAFE',                 // Light blue background
    
    // Background Colors - Grey tones
    'color-bg-primary': '#FFFFFF',                    // Pure white main background
    'color-bg-secondary': BRAND_PALETTE.LIGHT,        // #F8FAFC - Off-white grey background
    'color-bg-tertiary': '#F1F5F9',                  // Slightly darker grey background
    'color-bg-elevated': '#FFFFFF',                   // White for cards/elevated surfaces
    'color-bg-overlay': 'rgba(71, 85, 105, 0.5)',    // Blue-grey overlay
    
    // Surface Colors - Grey variations
    'color-surface-primary': '#FFFFFF',
    'color-surface-secondary': '#F8FAFC',
    'color-surface-elevated': '#FFFFFF',
    'color-surface-hover': '#F1F5F9',
    
    // Border Colors - Subtle greys
    'color-border-primary': '#E2E8F0',
    'color-border-secondary': '#CBD5E1',
    'color-border-focus': '#3B82F6',                  // Blue focus borders
    'color-border-error': '#EF4444',
    'color-border-success': '#10B981',
    
    // Text Colors - Professional greys
    'color-text-primary': '#0F172A',                  // Very dark blue-grey
    'color-text-secondary': '#475569',                // Medium blue-grey
    'color-text-tertiary': '#64748B',                 // Light blue-grey
    'color-text-quaternary': '#94A3B8',               // Very light blue-grey
    'color-text-inverse': '#FFFFFF',
    'color-text-disabled': '#CBD5E1',
    'color-text-link': '#3B82F6',                     // Professional blue links
    'color-text-link-hover': '#2563EB',               // Darker blue on hover
    
    // State Colors
    'color-success': '#10B981',
    'color-success-hover': '#059669',
    'color-success-subtle': '#D1FAE5',
    
    'color-warning': '#F59E0B',
    'color-warning-hover': '#D97706',
    'color-warning-subtle': '#FEF3C7',
    
    'color-error': '#EF4444',
    'color-error-hover': '#DC2626',
    'color-error-subtle': '#FEF2F2',
    
    'color-info': '#6B7280',                          // Grey-based info color
    'color-info-hover': '#4B5563',
    'color-info-subtle': '#F3F4F6',
    
    // NEW: Notification Colors
    'color-notification': '#3B82F6',                  // Blue notification accent
    'color-notification-hover': '#2563EB',            // Darker blue on hover
    'color-notification-subtle': '#EFF6FF',           // Very light blue background
    'color-notification-badge': '#EF4444',            // Red for unread count badge
    'color-notification-badge-text': '#FFFFFF',       // White text on badge
    'color-notification-read': '#6B7280',             // Grey for read notifications
    'color-notification-unread': '#1F2937',           // Dark for unread notifications
    'color-notification-border': '#E5E7EB',           // Light grey border
    
    // NEW: File Attachment Colors
    'color-attachment': '#10B981',                    // Green for attachments
    'color-attachment-hover': '#059669',              // Darker green on hover
    'color-attachment-subtle': '#ECFDF5',             // Light green background
    
    // Shadows
    'shadow-sm': '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    'shadow-md': '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    'shadow-lg': '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
  },
  
  dark: {
    // Navigation/UI Colors (dark mode greys)
    'color-navigation': '#1E293B',                    // Dark slate for navigation
    'color-navigation-hover': '#334155',              // Lighter on hover
    'color-navigation-active': '#475569',             // Even lighter for active
    'color-navigation-text': '#F8FAFC',               // Light grey text
    
    // Accent/Interactive Colors - Bright accent for dark mode
    'color-accent': '#60A5FA',                        // Light blue for dark mode
    'color-accent-hover': '#93C5FD',                  // Even lighter on hover
    'color-accent-active': '#DBEAFE',                 // Lightest for active
    'color-accent-subtle': '#1E3A8A',                 // Dark blue background
    
    // Background Colors - Dark greys
    'color-bg-primary': BRAND_PALETTE.DARK,           // #0F172A - Dark blue-grey main background
    'color-bg-secondary': '#1E293B',                  // Dark slate secondary
    'color-bg-tertiary': '#334155',                   // Lighter slate tertiary
    'color-bg-elevated': '#1E293B',                   // Dark slate for elevated surfaces
    'color-bg-overlay': 'rgba(0, 0, 0, 0.8)',        // Dark overlay
    
    // Surface Colors - Dark variations
    'color-surface-primary': '#1E293B',
    'color-surface-secondary': '#334155',
    'color-surface-elevated': '#475569',
    'color-surface-hover': '#475569',
    
    // Border Colors - Dark greys
    'color-border-primary': '#475569',
    'color-border-secondary': '#64748B',
    'color-border-focus': '#60A5FA',                  // Light blue focus
    'color-border-error': '#F87171',
    'color-border-success': '#34D399',
    
    // Text Colors - Light greys for readability
    'color-text-primary': '#F8FAFC',
    'color-text-secondary': '#E2E8F0',
    'color-text-tertiary': '#CBD5E1',
    'color-text-quaternary': '#94A3B8',
    'color-text-inverse': '#0F172A',
    'color-text-disabled': '#64748B',
    'color-text-link': '#93C5FD',                     // Light blue links
    'color-text-link-hover': '#DBEAFE',               // Even lighter on hover
    
    // State Colors - Adjusted for dark mode
    'color-success': '#34D399',
    'color-success-hover': '#6EE7B7',
    'color-success-subtle': '#064E3B',
    
    'color-warning': '#FBBF24',
    'color-warning-hover': '#FCD34D',
    'color-warning-subtle': '#78350F',
    
    'color-error': '#F87171',
    'color-error-hover': '#FCA5A5',
    'color-error-subtle': '#7F1D1D',
    
    'color-info': '#94A3B8',                          // Light grey info for dark mode
    'color-info-hover': '#CBD5E1',
    'color-info-subtle': '#334155',
    
    // NEW: Notification Colors (Dark Mode)
    'color-notification': '#60A5FA',                  // Light blue for dark mode
    'color-notification-hover': '#93C5FD',            // Even lighter blue
    'color-notification-subtle': '#1E3A8A',           // Dark blue background
    'color-notification-badge': '#F87171',            // Light red for dark mode
    'color-notification-badge-text': '#1F2937',       // Dark text on light badge
    'color-notification-read': '#9CA3AF',             // Light grey for read
    'color-notification-unread': '#F9FAFB',           // Light for unread
    'color-notification-border': '#4B5563',           // Dark grey border
    
    // NEW: File Attachment Colors (Dark Mode)
    'color-attachment': '#34D399',                    // Light green for dark mode
    'color-attachment-hover': '#6EE7B7',              // Even lighter green
    'color-attachment-subtle': '#064E3B',             // Dark green background
    
    // Shadows (stronger for dark mode)
    'shadow-sm': '0 1px 2px 0 rgb(0 0 0 / 0.3)',
    'shadow-md': '0 4px 6px -1px rgb(0 0 0 / 0.4), 0 2px 4px -2px rgb(0 0 0 / 0.3)',
    'shadow-lg': '0 10px 15px -3px rgb(0 0 0 / 0.4), 0 4px 6px -4px rgb(0 0 0 / 0.3)'
  }
};

// Theme modes
export const THEME_MODES = {
  LIGHT: 'light',
  DARK: 'dark'
};